import { ethers } from 'ethers';
import {
  ON_CHAIN_TOKEN_ADDRESSES,
  type OnChainTokenSymbol,
} from './blockchain';

const MAX_TRANSFER_COUNT = 30;
const ALCHEMY_API_KEY = import.meta.env.VITE_ALCHEMY_API_KEY?.trim();
const ALCHEMY_WORLD_CHAIN_URL = ALCHEMY_API_KEY
  ? `https://worldchain-mainnet.g.alchemy.com/v2/${encodeURIComponent(ALCHEMY_API_KEY)}`
  : null;

export type OnChainTransactionRecord = {
  hash: string;
  kind: 'transfer' | 'swap';
  tokenSymbol: OnChainTokenSymbol;
  tokenAddress: string;
  amount: string;
  from: string;
  to: string;
  direction: 'in' | 'out';
  blockNumber: number;
  timestamp: number | null;
  swapFromToken?: OnChainTokenSymbol;
  swapToToken?: OnChainTokenSymbol;
  swapFromAmount?: string;
  swapToAmount?: string;
};

export type OnChainTransactionHistoryCursor = {
  outgoingPageKey: string | null;
  incomingPageKey: string | null;
};

export type OnChainTransactionHistoryPage = {
  records: OnChainTransactionRecord[];
  nextCursor: OnChainTransactionHistoryCursor | null;
};

type TransferDirection = OnChainTransactionRecord['direction'];

type TransferRecordWithAsset = {
  asset: string;
  record: OnChainTransactionRecord;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function getAddress(value: unknown): string | null {
  const address = getNonEmptyString(value);
  if (!address || !ethers.isAddress(address)) return null;
  return ethers.getAddress(address);
}

function getDecimalPlaces(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  const decimal = getNonEmptyString(value);
  if (!decimal) return null;
  const parsed = /^0x[\da-f]+$/i.test(decimal)
    ? Number.parseInt(decimal, 16)
    : Number(decimal);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

function getAmount(value: unknown, rawContract: Record<string, unknown>): string | null {
  const rawValue = getNonEmptyString(rawContract.value);
  const decimals = getDecimalPlaces(rawContract.decimal);
  if (rawValue && decimals !== null && /^0x[\da-f]+$/i.test(rawValue)) {
    try {
      return ethers.formatUnits(BigInt(rawValue), decimals);
    } catch {
      return null;
    }
  }

  const amount = typeof value === 'number' ? String(value) : getNonEmptyString(value);
  if (!amount || !/^(?:\d+|\d*\.\d+)(?:e[+-]?\d+)?$/i.test(amount)) return null;

  const numericAmount = Number(amount);
  return Number.isFinite(numericAmount) && numericAmount >= 0 ? amount : null;
}

function getBlockNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  const blockNumber = getNonEmptyString(value);
  if (!blockNumber || !/^0x[\da-f]+$/i.test(blockNumber)) return null;

  const parsed = Number.parseInt(blockNumber, 16);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function getTimestampValue(value: unknown): number | null {
  const numericValue = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^0x[\da-f]+$/i.test(value.trim())
      ? Number.parseInt(value.trim(), 16)
      : typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value.trim())
        ? Number(value.trim())
        : null;

  if (numericValue !== null) {
    if (!Number.isFinite(numericValue) || numericValue < 0) return null;
    const milliseconds = numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue;
    return Number.isSafeInteger(milliseconds) ? milliseconds : null;
  }

  const dateValue = getNonEmptyString(value);
  if (!dateValue) return null;
  const timestamp = Date.parse(dateValue);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function getTimestamp(metadata: unknown): number | null {
  if (!isRecord(metadata)) return null;
  return getTimestampValue(metadata.blockTimestamp);
}

function getPageKey(response: unknown): string | null {
  if (!isRecord(response)) return null;
  return getNonEmptyString(response.pageKey);
}

async function fetchAlchemyRpc(method: string, params: unknown[]): Promise<unknown> {
  if (!ALCHEMY_WORLD_CHAIN_URL) {
    throw new Error('鏈上交易紀錄服務尚未設定。');
  }

  let response: Response;
  try {
    response = await fetch(ALCHEMY_WORLD_CHAIN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method,
        params,
      }),
    });
  } catch {
    throw new Error('鏈上交易紀錄服務暫時無法連線。');
  }

  if (!response.ok) {
    throw new Error('鏈上交易紀錄服務暫時無法使用。');
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new Error('鏈上交易紀錄服務回傳格式無效。');
  }

  if (!isRecord(payload)) {
    throw new Error('鏈上交易紀錄服務回傳格式無效。');
  }

  if (payload.error) {
    throw new Error('鏈上交易紀錄服務暫時無法使用。');
  }

  return payload.result;
}

async function fetchAssetTransfers(params: Record<string, unknown>): Promise<unknown> {
  return fetchAlchemyRpc('alchemy_getAssetTransfers', [params]);
}

async function fetchBlockTimestamp(blockNumber: number): Promise<number | null> {
  try {
    const block = await fetchAlchemyRpc(
      'eth_getBlockByNumber',
      [`0x${blockNumber.toString(16)}`, false],
    );
    return isRecord(block) ? getTimestampValue(block.timestamp) : null;
  } catch {
    return null;
  }
}

async function hydrateMissingTimestamps(
  records: TransferRecordWithAsset[],
): Promise<TransferRecordWithAsset[]> {
  const missingBlockNumbers = [...new Set(
    records
      .filter(({ record }) => record.timestamp === null)
      .map(({ record }) => record.blockNumber),
  )];
  if (missingBlockNumbers.length === 0) return records;

  const timestamps = await Promise.all(
    missingBlockNumbers.map(async (blockNumber) => [blockNumber, await fetchBlockTimestamp(blockNumber)] as const),
  );
  const timestampsByBlock = new Map(timestamps);

  return records.map((item) => {
    if (item.record.timestamp !== null) return item;
    return {
      ...item,
      record: {
        ...item.record,
        timestamp: timestampsByBlock.get(item.record.blockNumber) ?? null,
      },
    };
  });
}

function getTransferRecords(
  response: unknown,
  direction: TransferDirection,
  tokenSymbolsByAddress: ReadonlyMap<string, OnChainTokenSymbol>,
): TransferRecordWithAsset[] {
  if (!isRecord(response) || !Array.isArray(response.transfers)) {
    throw new Error('鏈上交易紀錄服務回傳格式無效。');
  }

  const records: TransferRecordWithAsset[] = [];

  for (const transfer of response.transfers) {
    if (!isRecord(transfer) || !isRecord(transfer.rawContract)) continue;

    const hash = getNonEmptyString(transfer.hash);
    const from = getAddress(transfer.from);
    const to = getAddress(transfer.to);
    const tokenAddress = getAddress(transfer.rawContract.address);
    const amount = getAmount(transfer.value, transfer.rawContract);
    const blockNumber = getBlockNumber(transfer.blockNum);

    if (!hash || !from || !to || !tokenAddress || !amount || blockNumber === null) continue;

    const tokenSymbolFromAddress = tokenSymbolsByAddress.get(tokenAddress.toLowerCase());
    if (!tokenSymbolFromAddress) continue;

    const asset = getNonEmptyString(transfer.asset);
    const tokenSymbolFromAsset = asset && Object.prototype.hasOwnProperty.call(ON_CHAIN_TOKEN_ADDRESSES, asset)
      ? asset as OnChainTokenSymbol
      : null;
    const tokenSymbol = tokenSymbolFromAsset === tokenSymbolFromAddress
      ? tokenSymbolFromAsset
      : tokenSymbolFromAddress;

    records.push({
      asset: (asset ?? tokenSymbol).toLowerCase(),
      record: {
        hash,
        kind: 'transfer',
        tokenSymbol,
        tokenAddress,
        amount,
        from,
        to,
        direction,
        blockNumber,
        timestamp: getTimestamp(transfer.metadata),
      },
    });
  }

  return records;
}

function getTransferKey(record: OnChainTransactionRecord, asset: string): string {
  return [
    record.hash.toLowerCase(),
    record.from.toLowerCase(),
    record.to.toLowerCase(),
    asset,
  ].join(':');
}

function getMaxTransferCount(limit: number | undefined): number {
  if (limit === undefined) return MAX_TRANSFER_COUNT;
  if (!Number.isSafeInteger(limit) || limit < 1) {
    throw new RangeError('交易紀錄數量必須是大於 0 的整數。');
  }

  return Math.min(limit, MAX_TRANSFER_COUNT);
}

export async function getOnChainTransactionHistoryPage(
  walletAddress: string,
  limit = MAX_TRANSFER_COUNT,
  cursor?: OnChainTransactionHistoryCursor | null,
): Promise<OnChainTransactionHistoryPage> {
  if (!ethers.isAddress(walletAddress)) {
    throw new Error('請輸入有效的錢包地址。');
  }

  const normalizedWalletAddress = ethers.getAddress(walletAddress);
  const maxTransferCount = getMaxTransferCount(limit);
  const tokenSymbolsByAddress = new Map<string, OnChainTokenSymbol>(
    Object.entries(ON_CHAIN_TOKEN_ADDRESSES).map(([symbol, address]) => [
      ethers.getAddress(address).toLowerCase(),
      symbol as OnChainTokenSymbol,
    ]),
  );
  const request = {
    fromBlock: '0x0',
    toBlock: 'latest',
    category: ['erc20'],
    contractAddresses: Object.values(ON_CHAIN_TOKEN_ADDRESSES),
    excludeZeroValue: true,
    order: 'desc',
    maxCount: `0x${maxTransferCount.toString(16)}`,
    withMetadata: true,
  } as const;

  const [outgoingResponse, incomingResponse] = await Promise.all([
    cursor && !cursor.outgoingPageKey
      ? Promise.resolve({ transfers: [] })
      : fetchAssetTransfers({
        ...request,
        fromAddress: normalizedWalletAddress,
        ...(cursor?.outgoingPageKey ? { pageKey: cursor.outgoingPageKey } : {}),
      }),
    cursor && !cursor.incomingPageKey
      ? Promise.resolve({ transfers: [] })
      : fetchAssetTransfers({
        ...request,
        toAddress: normalizedWalletAddress,
        ...(cursor?.incomingPageKey ? { pageKey: cursor.incomingPageKey } : {}),
      }),
  ]);

  const transactionHistory = await hydrateMissingTimestamps([
    ...getTransferRecords(outgoingResponse, 'out', tokenSymbolsByAddress),
    ...getTransferRecords(incomingResponse, 'in', tokenSymbolsByAddress),
  ]);
  const uniqueRecords = new Map<string, OnChainTransactionRecord>();

  for (const { asset, record } of transactionHistory) {
    const key = getTransferKey(record, asset);
    if (!uniqueRecords.has(key)) uniqueRecords.set(key, record);
  }

  const transfersByHash = new Map<string, OnChainTransactionRecord[]>();
  for (const record of uniqueRecords.values()) {
    const transfers = transfersByHash.get(record.hash) ?? [];
    transfers.push(record);
    transfersByHash.set(record.hash, transfers);
  }

  const transactionRecords: OnChainTransactionRecord[] = [];
  for (const transfers of transfersByHash.values()) {
    const hasSent = transfers.some(
      (record) => record.direction === 'out' && record.from.toLowerCase() === normalizedWalletAddress.toLowerCase(),
    );
    const hasReceived = transfers.some(
      (record) => record.direction === 'in' && record.to.toLowerCase() === normalizedWalletAddress.toLowerCase(),
    );
    const uniqueTokens = new Set(transfers.map((record) => record.tokenAddress.toLowerCase()));

    if (hasSent && hasReceived && transfers.length >= 2 && uniqueTokens.size >= 2) {
      const sentTransfers = transfers.filter(
        (record) => record.direction === 'out' && record.from.toLowerCase() === normalizedWalletAddress.toLowerCase(),
      );
      const receivedTransfers = transfers.filter(
        (record) => record.direction === 'in' && record.to.toLowerCase() === normalizedWalletAddress.toLowerCase(),
      );
      const fromTransfer = sentTransfers[0];
      const toTransfer = receivedTransfers.find(
        (record) => record.tokenAddress.toLowerCase() !== fromTransfer?.tokenAddress.toLowerCase(),
      ) ?? receivedTransfers[0];

      if (fromTransfer && toTransfer) {
        transactionRecords.push({
          ...toTransfer,
          kind: 'swap',
          from: fromTransfer.from,
          to: toTransfer.to,
          swapFromToken: fromTransfer.tokenSymbol,
          swapToToken: toTransfer.tokenSymbol,
          swapFromAmount: fromTransfer.amount,
          swapToAmount: toTransfer.amount,
          timestamp: toTransfer.timestamp ?? fromTransfer.timestamp,
        });
        continue;
      }
    }

    transactionRecords.push(...transfers);
  }

  const records = transactionRecords
    .sort((left, right) => right.blockNumber - left.blockNumber)
    .slice(0, maxTransferCount * 2);
  const outgoingPageKey = getPageKey(outgoingResponse);
  const incomingPageKey = getPageKey(incomingResponse);

  return {
    records,
    nextCursor: outgoingPageKey || incomingPageKey
      ? { outgoingPageKey, incomingPageKey }
      : null,
  };
}

export async function getOnChainTransactionHistory(
  walletAddress: string,
  limit?: number,
): Promise<OnChainTransactionRecord[]> {
  const page = await getOnChainTransactionHistoryPage(walletAddress, limit);
  return page.records.slice(0, getMaxTransferCount(limit));
}
